import { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import ExpandMore from "@material-ui/icons/ExpandMore";
import BaseHead from "../templates/BaseHead";
import BottomNavigationBar from "../organisms/BottomNavigationBar";
import { getSubdomain } from "../../utils/getSubdomain";
import { routes } from "../../router/Router";
import STUtils from "../../assets/styles/utility.module.scss";
import ST from "../../assets/styles/organisms/chatMessageHistory.module.scss";
import { useMessageHistory } from "../../hooks/useMessageHistory";
import { SiteContext } from "../../providers/SiteProvider";
import { CheckSpFlag } from "../../hooks/CheckSpFlag";
import { getChannelMemberName } from "../organisms/messageHistoryDisplay";
import ChatHistorySearchBox from "../molecules/ChatHistorySearchBox";
import ChatMessageHistoryList from "../organisms/ChatMessageHistoryList";
import SnackbarMessage from "../atoms/Snackbar";

/**
 * チャット過去履歴ページ
 * path: channel_id / query: from_date, message_id
 */
const ChatMessageHistory = () => {
  const subdomain = getSubdomain(window.location.hostname);
  const { channel_id } = useParams<{ channel_id: string }>();
  const query = new URLSearchParams(useLocation().search);
  const channelId = Number(channel_id ?? 0);
  const fromDate = query.get("from_date");
  const messageId = query.get("message_id");
  const isMobile = CheckSpFlag();
  const site = useContext(SiteContext);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // SP参加者アコーディオン（デフォルト閉じる）
  const [isMembersOpen, setIsMembersOpen] = useState(false);

  const {
    channel,
    sendbirdUsers,
    data,
    isLoading,
    containerRef,
    sentinelRef,
    downloadFile,
    handleSearch,
    errorMessage,
    clearError,
  } = useMessageHistory({
    channelId,
    messageIdParam: messageId,
    fromDateParam: fromDate,
  });

  useEffect(() => {
    if (errorMessage) {
      setSnackbarOpen(true);
    }
  }, [errorMessage]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    clearError();
  };

  return (
    <BaseHead
      title={routes.chatMessageHistory.title}
      login={true}
      subdomain={subdomain}
      hasBackground
      hasNavigationBar={true}
      hasBackButton={true}
      showFooter={!isMobile}
    >
      <section className={STUtils.container_large}>
        <div className={ST.history_page}>
          <div className={ST.channel_card} key="channel_info">
            <div className={ST.channel_info_box}>
              <div className={ST.channel_name}>
                {channel?.channel_name ?? ""}
              </div>
              <ChatHistorySearchBox
                placeholder="チャット履歴を検索"
                onClick={handleSearch}
              />
            </div>
            <div className={ST.member_list}>
              {isMobile ? (
                <button
                  type="button"
                  className={ST.member_list_toggle}
                  onClick={() => setIsMembersOpen((prev) => !prev)}
                  aria-expanded={isMembersOpen}
                  aria-controls="channel-member-list"
                >
                  <span className={ST.member_list_label}>
                    参加者({channel?.chat_channel_members?.length ?? 0})
                  </span>
                  <ExpandMore
                    className={`${ST.member_list_toggle_icon} ${
                      isMembersOpen ? ST.member_list_toggle_icon_open : ""
                    }`}
                  />
                </button>
              ) : (
                <div className={ST.member_list_label}>
                  参加者({channel?.chat_channel_members?.length ?? 0})
                </div>
              )}
              {(!isMobile || isMembersOpen) && (
                <div
                  id="channel-member-list"
                  className={ST.member_list_box}
                >
                  {channel?.chat_channel_members
                    ?.map((user) => ({
                      user,
                      name: getChannelMemberName(
                        user,
                        sendbirdUsers,
                        site?.name,
                      ),
                    }))
                    .filter(
                      (
                        item,
                      ): item is {
                        user: typeof item.user;
                        name: NonNullable<typeof item.name>;
                      } => Boolean(item.name),
                    )
                    .map(({ user, name }) => (
                      <div
                        className={ST.member_name}
                        key={"channel_info_" + user.sendbird_user_id}
                      >
                        {name}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {isLoading && (
            <div className={ST.loading}>
              <CircularProgress color="inherit" size={16} disableShrink /> <span>メッセージを読み込み中</span>
            </div>
          )}

          <div ref={containerRef} className={ST.message_list_scroll}>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {data.length > 0 ? (
              <ChatMessageHistoryList
                data={data}
                siteName={site?.name}
                downloadFile={downloadFile}
              />
            ) : (
              <>
                {!isLoading && (
                  <div className={ST.empty}>チャットの過去履歴がありません</div>
                )}
              </>
            )}
          </div>
        </div>
        <BottomNavigationBar />
      </section>
      <SnackbarMessage
        open={snackbarOpen}
        severity="error"
        message={errorMessage ?? undefined}
        handleClose={handleCloseSnackbar}
      />
    </BaseHead>
  );
};

export default ChatMessageHistory;
